import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  IconButton,
  Button,
  Divider,
  Grid2,
  Collapse,
  Alert,
  Tooltip,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HandymanIcon from '@mui/icons-material/Handyman';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import RecommendIcon from '@mui/icons-material/Recommend';
import PhotoIcon from '@mui/icons-material/Photo';
import ImageSlider from './ImageSlider';
import { useHomeAttachmentsContext } from '../contexts/HomeAttachmentsContext';
import { useHomeRepairsContext } from '../contexts/HomeRepairsContext';
import { PieChart } from '@mui/x-charts/PieChart';
import { RepairItem } from '../types/repairs';

interface RepairCostEstimationProps {
  onClose: () => void;
}

const RepairCostEstimation: React.FC<RepairCostEstimationProps> = ({ onClose }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('all');
  const { getAttachmentById, getAttachmentUrl } = useHomeAttachmentsContext();
  
  // Use the repairs context instead of passed estimates
  const { 
    repairs, 
    isLoading, 
    error,
    getRepairsSummary,
    toggleRepairResolution
  } = useHomeRepairsContext();

  // Get centralized calculations from context
  const { 
    costRange,
    averageRepairCost
  } = getRepairsSummary();

  // Calculate total cost dynamically from repair cost ranges - now using context data
  const totalCost = averageRepairCost;
  const totalMin = costRange.min;
  const totalMax = costRange.max;

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'high': return <WarningIcon fontSize="small" />;
      case 'medium': return <InfoIcon fontSize="small" />;
      case 'low': return <CheckCircleIcon fontSize="small" />;
      default: return <InfoIcon fontSize="small" />;
    }
  };

  /*
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'electrical': return '⚡';
      case 'plumbing': return '🔧';
      case 'hvac': return '🌡️';
      case 'structural': return '🏗️';
      case 'cosmetic': return '🎨';
      case 'exterior': return '🏠';
      default: return '🔨';
    }
  };
  */

  const formatTimeframe = (timeframe: string) => {
    const timeframes: { [key: string]: string } = {
      'immediate': 'ASAP',
      'within_month': 'This Month',
      'within_3_months': '3 Months',
      'within_6_months': '6 Months',
      'within_year': 'This Year'
    };
    return timeframes[timeframe] || timeframe;
  };

  const handleContactClick = (type: 'phone' | 'email', value: string) => {
    if (type === 'phone') {
      window.open(`tel:${value}`);
    } else if (type === 'email') {
      window.open(`mailto:${value}`);
    }
  };

  const handleWebsiteClick = (website: string) => {
    // Ensure URL has protocol
    const url = website.startsWith('http') ? website : `https://${website}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Get images for a specific repair
  const getRepairImages = (repair: RepairItem): string[] => {
    if (!repair.related_image_ids || repair.related_image_ids.length === 0) {
      return [];
    }

    return repair.related_image_ids
      .map(imageId => {
        const attachment = getAttachmentById(imageId);
        return attachment ? getAttachmentUrl(attachment) : null;
      })
      .filter((url): url is string => url !== null);
  };

  // Convert timeframe_months to timeframe string for filtering
  const getTimeframeString = (months: number): string => {
    if (months === 0) return 'immediate';
    if (months <= 1) return 'within_month';
    if (months <= 3) return 'within_3_months';
    if (months <= 6) return 'within_6_months';
    return 'within_year';
  };

  // Update filteredRepairs to handle new field names and DIY filtering
  const filteredRepairs = selectedTimeframe === 'all' 
    ? repairs 
    : selectedTimeframe === 'immediate'
    ? repairs.filter(repair => repair.urgency === 'high')
    : selectedTimeframe === 'diy_friendly'
    ? repairs.filter(repair => repair.diy_friendly)
    : repairs.filter(repair => getTimeframeString(repair.timeframe_months) === selectedTimeframe);

  const urgentRepairs = repairs.filter(repair => repair.urgency === 'high');
  const diyRepairs = repairs.filter(repair => repair.diy_friendly);

  const getUrgencyDistribution = () => {
    const urgencyCounts = repairs.reduce((acc, repair) => {
      acc[repair.urgency] = (acc[repair.urgency] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return [
      { id: 'high', value: urgencyCounts.high || 0, label: 'High', color: '#d32f2f' },
      { id: 'medium', value: urgencyCounts.medium || 0, label: 'Medium', color: '#ed6c02' },
      { id: 'low', value: urgencyCounts.low || 0, label: 'Low', color: '#2e7d32' }
    ].filter(item => item.value > 0);
  };

  // Update handler for urgent items card click to use 'immediate'
  const handleUrgentItemsClick = () => {
    setSelectedTimeframe('immediate');
  };

  // Add handler for DIY friendly items card click
  const handleDiyItemsClick = () => {
    setSelectedTimeframe('diy_friendly');
  };

  // Add handler for clear filters
  const handleClearFilters = () => {
    setSelectedTimeframe('all');
  };

  // Handle loading and error states
  if (isLoading) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'warning.light',
          color: 'warning.contrastText'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon />
            <Typography variant="h6" fontWeight="bold">
              Repair Cost Analysis
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Loading state */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Typography>Loading repairs...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'warning.light',
          color: 'warning.contrastText'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon />
            <Typography variant="h6" fontWeight="bold">
              Repair Cost Analysis
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Error state */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 2
        }}>
          <Alert severity="error">
            Error loading repairs: {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  if (repairs.length === 0) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'warning.light',
          color: 'warning.contrastText'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon />
            <Typography variant="h6" fontWeight="bold">
              Repair Cost Analysis
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Empty state with better visual design */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 4,
          backgroundColor: 'background.default'
        }}>
          <Box sx={{ 
            textAlign: 'center',
            maxWidth: 400,
            p: 3,
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            boxShadow: 1
          }}>
            <Box sx={{ mb: 2 }}>
              <BuildIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            </Box>
            <Typography variant="h6" gutterBottom color="text.primary">
              No Repairs Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Start a conversation with our AI assistant to analyze your home photos and identify potential repair items.
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Upload photos and chat with the AI to get started with your home analysis.
              </Typography>
            </Alert>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'warning.light',
        color: 'warning.contrastText'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon />
          <Typography variant="h6" fontWeight="bold">
            Repair Cost Analysis
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
        <Grid2 container spacing={2}>
          <Grid2 size={3}>
            <Card sx={{ 
              textAlign: 'center', 
              backgroundColor: 'warning.light', 
              color: 'warning.contrastText',
              border: 1, 
              borderColor: 'divider',
              height: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold">
                  ${totalCost.toLocaleString()}
                </Typography>
                <Typography variant="caption">
                  Estimated Total Cost
                </Typography>
              </CardContent>
            </Card>
          </Grid2>
          <Grid2 size={3}>
            <Card sx={{ 
              textAlign: 'center', 
              backgroundColor: 'error.light', 
              color: 'error.contrastText',
              border: 1, 
              borderColor: 'divider',
              height: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
            onClick={handleUrgentItemsClick}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold">
                  {urgentRepairs.length}
                </Typography>
                <Typography variant="caption">
                  Urgent Items
                </Typography>
              </CardContent>
            </Card>
          </Grid2>
          <Grid2 size={3}>
            <Card sx={{ 
              textAlign: 'center', 
              backgroundColor: 'success.light', 
              color: 'success.contrastText',
              border: 1, 
              borderColor: 'divider',
              height: 120, // Set fixed height for all cards
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
            onClick={handleDiyItemsClick}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold">
                  {diyRepairs.length}
                </Typography>
                <Typography variant="caption">
                  DIY Friendly
                </Typography>
              </CardContent>
            </Card>
          </Grid2>
          <Grid2 size={3}>
            <Card sx={{ 
              textAlign: 'center', 
              backgroundColor: 'background.paper', 
              border: 1, 
              borderColor: 'divider',
              height: 120, // Same height as others
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <CardContent sx={{ py: 1.5, px: 1, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
                <Box sx={{ height: 60, width: '100%', mb: 1 }}> {/* Chart at top */}
                  <PieChart
                    series={[{
                      data: getUrgencyDistribution(),
                      innerRadius: 15,
                      outerRadius: 25,
                      paddingAngle: 2,
                      cornerRadius: 2,
                    }]}
                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                    slotProps={{
                      legend: { hidden: true }
                    }}
                  />
                </Box>
                <Typography variant="caption">
                  Repair Urgency
                </Typography>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

        {/* Cost Range Bar */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Cost Range: ${totalMin.toLocaleString()} - ${totalMax.toLocaleString()}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={75} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      </Box>

      {/* Filter Buttons */}
      <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {['all', 'immediate', 'diy_friendly', 'within_month', 'within_3_months', 'within_6_months'].map((timeframe) => (
            <Button
              key={timeframe}
              variant={selectedTimeframe === timeframe ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setSelectedTimeframe(timeframe)}
              sx={{ borderRadius: '16px', textTransform: 'none' }}
            >
              {timeframe === 'all' ? 'All Items' : 
               timeframe === 'immediate' ? 'Urgent' :
               timeframe === 'diy_friendly' ? 'DIY Friendly' :
               formatTimeframe(timeframe)}
            </Button>
          ))}
        </Box>
        
        {/* Clear Filter Button */}
        {selectedTimeframe !== 'all' && (
          <Button
            variant="text"
            size="small"
            onClick={handleClearFilters}
            sx={{ 
              borderRadius: '16px', 
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            Clear Filter
          </Button>
        )}
      </Box>

      {/* Repair Items List */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        px: 2,
        pb: 2
      }}>
        {filteredRepairs.map((repair, _index) => {
          const repairImages = getRepairImages(repair);
          
          return (
            <Card key={repair.id} sx={{ mb: 1.5, border: 1, borderColor: 'divider' }}>
              <CardContent sx={{ p: 2 }}>
                {/* Header Row */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  mb: 1 
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      {repair.item}
                    </Typography>
                    {repair.diy_friendly && (
                      <Tooltip title="DIY Friendly">
                        <HandymanIcon color="success" fontSize="small" />
                      </Tooltip>
                    )}
                    {repair.referrals && repair.referrals.length > 0 && (
                      <Tooltip title={`${repair.referrals.length} referral${repair.referrals.length > 1 ? 's' : ''} available`}>
                        <RecommendIcon color="primary" fontSize="small" />
                      </Tooltip>
                    )}
                    {repairImages.length > 0 && (
                      <Tooltip title={`${repairImages.length} related image${repairImages.length > 1 ? 's' : ''}`}>
                        <PhotoIcon color="secondary" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      ${Math.round((repair.cost_range[0] + repair.cost_range[1]) / 2).toLocaleString()}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => toggleExpanded(repair.id)}
                    >
                      {expandedItems.has(repair.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Chips Row */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Resolution Status Chip */}
                  <Chip 
                    icon={repair.is_resolved ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
                    label={repair.is_resolved ? 'RESOLVED' : 'UNRESOLVED'} 
                    color={repair.is_resolved ? 'success' : 'default'}
                    variant={repair.is_resolved ? 'filled' : 'outlined'}
                    size="small"
                    clickable
                    onClick={() => toggleRepairResolution(repair.id)}
                  />
                  <Chip 
                    icon={getUrgencyIcon(repair.urgency)}
                    label={repair.urgency.toUpperCase()} 
                    color={getUrgencyColor(repair.urgency) as any}
                    size="small"
                  />
                  <Chip 
                    label={repair.category} 
                    variant="outlined" 
                    size="small"
                  />
                  <Chip 
                    label={formatTimeframe(getTimeframeString(repair.timeframe_months))} 
                    color="info"
                    variant="outlined"
                    size="small"
                  />
                  {repair.area_name && (
                    <Chip 
                      label={repair.area_name} 
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {repair.referrals && repair.referrals.length > 0 && (
                    <Chip 
                      icon={<RecommendIcon />}
                      label={`${repair.referrals.length} Referral${repair.referrals.length > 1 ? 's' : ''}`} 
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {repairImages.length > 0 && (
                    <Chip 
                      icon={<PhotoIcon />}
                      label={`${repairImages.length} Image${repairImages.length > 1 ? 's' : ''}`} 
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>

                {/* Description */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {repair.description}
                </Typography>

                {/* Related Images Preview (when collapsed) */}
                {!expandedItems.has(repair.id) && repairImages.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhotoIcon fontSize="small" />
                        Related Images:
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      overflowX: 'auto',
                      pb: 1,
                      '&::-webkit-scrollbar': { height: 4 },
                      '&::-webkit-scrollbar-thumb': { 
                        backgroundColor: 'rgba(0,0,0,0.2)', 
                        borderRadius: 2 
                      }
                    }}>
                      {repairImages.slice(0, 4).map((imageUrl, index) => (
                        <Box
                          key={index}
                          sx={{
                            minWidth: 60,
                            height: 60,
                            borderRadius: 1,
                            overflow: 'hidden',
                            flexShrink: 0,
                            border: 1,
                            borderColor: 'divider'
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={`Issue evidence ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </Box>
                      ))}
                      {repairImages.length > 4 && (
                        <Box
                          sx={{
                            minWidth: 60,
                            height: 60,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'grey.100',
                            border: 1,
                            borderColor: 'divider'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            +{repairImages.length - 4}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Expanded Details */}
                <Collapse in={expandedItems.has(repair.id)}>
                  <Divider sx={{ my: 1 }} />
                  <Grid2 container spacing={2}>
                    <Grid2 size={6}>
                      <Typography variant="caption" color="text.secondary">
                        Cost Range
                      </Typography>
                      <Typography variant="body2">
                        ${repair.cost_range[0]} - ${repair.cost_range[1]}
                      </Typography>
                    </Grid2>
                    <Grid2 size={6}>
                      <Typography variant="caption" color="text.secondary">
                        Urgency Level
                      </Typography>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {repair.urgency}
                      </Typography>
                    </Grid2>
                  </Grid2>

                  {/* Related Images Slider (when expanded) */}
                  {repairImages.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhotoIcon fontSize="small" />
                        Issue Evidence ({repairImages.length} image{repairImages.length > 1 ? 's' : ''})
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <ImageSlider
                          images={repairImages}
                          height={120}
                        />
                      </Box>
                    </>
                  )}

                  {/* Referrals Section */}
                  {repair.referrals && repair.referrals.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RecommendIcon fontSize="small" />
                        Recommended Professionals
                      </Typography>
                      
                      {repair.referrals.map((referral) => (
                        <Card key={referral.id} sx={{ mb: 1.5, backgroundColor: 'background.default', border: 1, borderColor: 'primary.light' }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              {/* Avatar */}
                              <Avatar 
                                src={referral.profileImage} 
                                sx={{ 
                                  bgcolor: 'primary.main',
                                  width: 48,
                                  height: 48
                                }}
                              >
                                {referral.name.charAt(0)}
                              </Avatar>
                              
                              {/* Referral Info */}
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {referral.name}
                                  </Typography>
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {referral.company}
                                </Typography>
                                
                                {/* Specialties */}
                                {referral.specialties && referral.specialties.length > 0 && (
                                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                                    {referral.specialties.map((specialty, index) => (
                                      <Chip 
                                        key={index}
                                        label={specialty} 
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                      />
                                    ))}
                                  </Box>
                                )}
                                
                                {/* Description */}
                                {referral.description && (
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    {referral.description}
                                  </Typography>
                                )}
                                
                                {/* Agent Notes */}
                                {referral.agentNotes && (
                                  <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                                    <Typography variant="caption">
                                      <strong>Agent Note:</strong> {referral.agentNotes}
                                    </Typography>
                                  </Alert>
                                )}
                                
                                {/* Contact Actions */}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {referral.phone && (
                                    <Button
                                      size="small"
                                      startIcon={<PhoneIcon />}
                                      onClick={() => handleContactClick('phone', referral.phone!)}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      {referral.phone}
                                    </Button>
                                  )}
                                  {referral.email && (
                                    <Button
                                      size="small"
                                      startIcon={<EmailIcon />}
                                      onClick={() => handleContactClick('email', referral.email!)}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Email
                                    </Button>
                                  )}
                                  {referral.website && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleWebsiteClick(referral.website!)}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Website
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Footer */}
      <Box sx={{ 
        p: 2, 
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
          <Typography variant="caption">
            <strong>AI-Generated Estimates:</strong> These costs are estimates based on image analysis and regional pricing data. 
            Get professional quotes for accurate pricing.
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default RepairCostEstimation;